import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Search, Printer, X, Calculator as CalcIcon, Plus, Minus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "0";
  return (Math.round(n * 10000) / 10000).toLocaleString("ro-RO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

export default function RecipeCalculator() {
  const { token, username, logout } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [quantities, setQuantities] = useState({}); // {recipe_id: "10"}
  const [notes, setNotes] = useState([
    { title: "Malaxare", content: "" },
    { title: "Coacere / Dospire", content: "" },
    { title: "Observații", content: "" },
  ]);
  const [showPrint, setShowPrint] = useState(false);
  const printRef = useRef(null);

  const authAxios = useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use((cfg) => {
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      return cfg;
    });
    return inst;
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authAxios.get("/recipes");
        setRecipes(data || []);
      } catch (e) {
        toast.error("Eroare la încărcare rețete");
      } finally {
        setLoading(false);
      }
    })();
  }, [authAxios]);

  const filteredRecipes = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return recipes;
    return recipes.filter((r) => {
      if (r.name.toLowerCase().includes(s)) return true;
      return (r.ingredients || []).some((ing) => (ing.name || "").toLowerCase().includes(s));
    });
  }, [recipes, search]);

  // Recipes activated (with qty > 0)
  const activeRecipes = useMemo(() => {
    return recipes
      .map((r) => ({ recipe: r, qty: parseFloat(quantities[r.id]) || 0 }))
      .filter((x) => x.qty > 0);
  }, [recipes, quantities]);

  const totalActive = activeRecipes.length;

  const setQty = (id, v) => {
    setQuantities((q) => ({ ...q, [id]: v }));
  };

  const incQty = (id, step = 1) => {
    const cur = parseFloat(quantities[id]) || 0;
    setQty(id, String(Math.max(0, cur + step)));
  };

  const resetAll = () => {
    setQuantities({});
    toast.info("Cantitățile au fost resetate");
  };

  const updateNote = (idx, field, value) => {
    setNotes((arr) => arr.map((n, i) => (i === idx ? { ...n, [field]: value } : n)));
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 flex flex-col" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 px-6 py-3 flex items-center justify-between no-print">
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="back-to-dashboard" className="text-neutral-500 hover:text-amber-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <CalcIcon className="w-5 h-5 text-amber-700" />
          <div>
            <h1 className="text-base font-bold tracking-tight">Calculator rețetă</h1>
            <p className="text-[10px] text-neutral-500 uppercase tracking-wider">Introdu cantități și printează fișa de producție</p>
          </div>
          <nav className="hidden md:flex items-center gap-1 ml-6 border-l border-neutral-200 pl-4">
            <Link to="/" data-testid="nav-dashboard" className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-700">Producție Zi</Link>
            <Link to="/inventar" data-testid="nav-inventar" className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-700">Inventar</Link>
            <Link to="/rapoarte" data-testid="nav-rapoarte" className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-700">Rapoarte</Link>
            <Link to="/calculator" data-testid="nav-calculator" className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 font-semibold">Calculator</Link>
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-neutral-500 uppercase tracking-wider">{totalActive} rețete active</span>
          <button
            data-testid="reset-button"
            onClick={resetAll}
            disabled={totalActive === 0}
            className="text-xs px-3 py-1.5 rounded-md border border-neutral-200 text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
          >
            Resetează
          </button>
          <button
            data-testid="print-button"
            onClick={() => setShowPrint(true)}
            disabled={totalActive === 0}
            className="text-xs px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-semibold inline-flex items-center gap-1.5"
          >
            <Printer className="w-3.5 h-3.5" />
            Tipărește fișa
          </button>
          <button data-testid="logout-button" onClick={logout} className="text-[10px] text-neutral-400 hover:text-neutral-900 uppercase tracking-wider ml-2">
            Ieșire ({username})
          </button>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-0 no-print">
        {/* LEFT: recipe list */}
        <section className="bg-white border-r border-neutral-200 flex flex-col">
          <div className="px-5 py-3 border-b border-neutral-100">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="recipe-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Caută rețetă sau ingredient..."
                className="w-full rounded-md border border-neutral-200 bg-neutral-50 pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white"
              />
            </div>
            <div className="text-[10px] text-neutral-400 mt-1">{filteredRecipes.length} din {recipes.length} rețete</div>
          </div>
          <div className="flex-1 overflow-y-auto thin-scroll px-2 py-2" data-testid="recipe-list">
            {loading && <div className="text-center text-xs text-neutral-400 py-6">Se încarcă...</div>}
            {!loading && filteredRecipes.map((r) => {
              const qty = quantities[r.id] || "";
              const active = qty && parseFloat(qty) > 0;
              return (
                <div
                  key={r.id}
                  className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md ${active ? "bg-amber-50" : "hover:bg-neutral-50"}`}
                  data-testid={`recipe-row-${r.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm truncate ${active ? "text-amber-900 font-semibold" : "text-neutral-700"}`}>{r.name}</div>
                    <div className="text-[10px] text-neutral-400 uppercase tracking-wider">{r.unit} · {r.ingredients.length} ing.</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => incQty(r.id, -1)}
                      className="w-6 h-6 rounded border border-neutral-200 hover:bg-neutral-100 flex items-center justify-center text-neutral-500"
                      data-testid={`dec-${r.id}`}
                      title="-1"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <input
                      data-testid={`qty-${r.id}`}
                      type="number"
                      min="0"
                      step="1"
                      value={qty}
                      onChange={(e) => setQty(r.id, e.target.value)}
                      className="w-16 text-center text-sm tabular-nums rounded border border-neutral-200 focus:outline-none focus:ring-1 focus:ring-amber-500 px-1.5 py-1"
                      placeholder="0"
                    />
                    <button
                      onClick={() => incQty(r.id, 1)}
                      className="w-6 h-6 rounded border border-neutral-200 hover:bg-neutral-100 flex items-center justify-center text-neutral-500"
                      data-testid={`inc-${r.id}`}
                      title="+1"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
            {!loading && filteredRecipes.length === 0 && (
              <div className="text-center text-xs text-neutral-400 py-6">Nicio rețetă găsită</div>
            )}
          </div>
        </section>

        {/* RIGHT: 3 notes + preview */}
        <aside className="bg-neutral-50 px-5 py-4 overflow-y-auto thin-scroll">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-2">Note pentru fișă</div>
          <div className="space-y-3">
            {notes.map((n, idx) => (
              <div key={idx} className="bg-white rounded-md border border-neutral-200 p-3" data-testid={`note-${idx}`}>
                <input
                  type="text"
                  value={n.title}
                  onChange={(e) => updateNote(idx, "title", e.target.value)}
                  placeholder={`Titlu ${idx + 1}`}
                  className="w-full text-sm font-semibold mb-1.5 bg-transparent focus:outline-none border-b border-neutral-100 pb-1"
                  data-testid={`note-title-${idx}`}
                />
                <textarea
                  value={n.content}
                  onChange={(e) => updateNote(idx, "content", e.target.value)}
                  placeholder="Detalii..."
                  rows={4}
                  maxLength={1000}
                  className="w-full text-xs bg-transparent focus:outline-none resize-none text-neutral-700"
                  data-testid={`note-content-${idx}`}
                />
                <div className="text-[9px] text-neutral-300 text-right">{n.content.length}/1000</div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-neutral-200 text-[10px] text-neutral-500 leading-relaxed">
            💡 <b>Cum funcționează:</b> Introdu cantitatea (nr. bucăți) la fiecare rețetă pe care vrei să o produci.
            Calculatorul înmulțește automat ingredientele rețetei cu numărul de bucăți și le afișează în fișă.
            Adaugă note (malaxare, coacere etc.) pe coloana din dreapta. Apasă <b>Tipărește fișa</b> pentru imprimare A4.
          </div>
        </aside>
      </div>

      {showPrint && (
        <PrintRecipeFisa
          activeRecipes={activeRecipes}
          notes={notes}
          onClose={() => setShowPrint(false)}
        />
      )}
    </div>
  );
}

function PrintRecipeFisa({ activeRecipes, notes, onClose }) {
  const handlePrint = () => window.print();
  const now = new Date();
  const dateLabel = now.toLocaleDateString("ro-RO");
  const visibleNotes = notes.filter((n) => (n.content || "").trim());

  return (
    <>
      <div className="no-print fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold">Previzualizare fișă rețetă</h3>
              <p className="text-[11px] text-neutral-500 mt-0.5">{activeRecipes.length} rețete · A4</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handlePrint} data-testid="confirm-print" className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 hover:bg-amber-700 px-3 py-2 text-xs font-semibold text-white">
                <Printer className="w-3.5 h-3.5" />
                Tipărește
              </button>
              <button onClick={onClose} data-testid="close-print" className="text-neutral-400 hover:text-neutral-900 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto thin-scroll p-6 bg-neutral-100">
            <div className="bg-white shadow-sm mx-auto" style={{ maxWidth: "210mm", padding: "12mm" }}>
              <FisaBody activeRecipes={activeRecipes} visibleNotes={visibleNotes} dateLabel={dateLabel} />
            </div>
          </div>
        </div>
      </div>

      {createPortal(
        <div className="hidden print:block print-area">
          <FisaBody activeRecipes={activeRecipes} visibleNotes={visibleNotes} dateLabel={dateLabel} />
        </div>,
        document.body
      )}
    </>
  );
}

function FisaBody({ activeRecipes, visibleNotes, dateLabel }) {
  return (
    <div className="text-neutral-900 fisa-print" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="flex items-end justify-between mb-5 pb-3 border-b-2 border-neutral-900 print-avoid-break">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Fișă rețetă · Producție</h1>
          <p className="text-sm text-neutral-700 mt-0.5">Brutăria LaDenis · {activeRecipes.length} {activeRecipes.length === 1 ? "rețetă" : "rețete"}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-neutral-500 uppercase tracking-wider">Data</p>
          <p className="text-lg font-bold">{dateLabel}</p>
        </div>
      </div>

      {activeRecipes.map(({ recipe, qty }, idx) => (
        <div key={recipe.id} className="mb-6 print-avoid-break">
          <div className="flex items-end justify-between border-b-2 border-neutral-700 pb-1 mb-2">
            <h2 className="text-xl font-bold capitalize">
              {idx + 1}. {recipe.name}
            </h2>
            <div className="text-lg">
              <span className="text-xs text-neutral-500 uppercase tracking-wider mr-1">Cantitate:</span>
              <span className="font-bold text-amber-700 tabular-nums">{fmt(qty)} {recipe.unit}</span>
            </div>
          </div>
          <table className="w-full border-collapse print-table-large">
            <thead>
              <tr>
                <th className="border-2 border-neutral-700 px-2 py-1.5 text-left w-10 text-sm font-bold uppercase">#</th>
                <th className="border-2 border-neutral-700 px-2 py-1.5 text-left text-sm font-bold uppercase">Ingredient</th>
                <th className="border-2 border-neutral-700 px-2 py-1.5 text-right w-32 text-sm font-bold uppercase">Cant / buc.</th>
                <th className="border-2 border-neutral-700 px-2 py-1.5 text-right w-36 text-sm font-bold uppercase">Total necesar</th>
                <th className="border-2 border-neutral-700 px-2 py-1.5 text-left w-20 text-sm font-bold uppercase">UM</th>
              </tr>
            </thead>
            <tbody>
              {(recipe.ingredients || []).map((ing, j) => {
                const perUnit = parseFloat(ing.qty_per_unit ?? ing.quantity) || 0;
                const total = perUnit * qty;
                return (
                  <tr key={`${recipe.id}-${j}`}>
                    <td className="border border-neutral-500 px-2 py-1.5 text-neutral-500 text-base">{j + 1}</td>
                    <td className="border border-neutral-500 px-2 py-1.5 capitalize text-base font-medium">{ing.name}</td>
                    <td className="border border-neutral-500 px-2 py-1.5 text-right tabular-nums text-neutral-700 text-base">{fmt(perUnit)}</td>
                    <td className="border border-neutral-500 px-2 py-1.5 text-right tabular-nums font-bold text-lg">{fmt(total)}</td>
                    <td className="border border-neutral-500 px-2 py-1.5 text-neutral-700 text-base">{ing.unit || "kg"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {visibleNotes.length > 0 && (
        <div className="mt-6 print-avoid-break">
          {visibleNotes.map((n, idx) => (
            <div key={idx} className="mb-4 border-2 border-neutral-500 rounded p-3">
              <div className="text-base font-bold uppercase tracking-wider text-neutral-900 mb-2 border-b-2 border-neutral-300 pb-1">
                {n.title || `Notă ${idx + 1}`}
              </div>
              <div className="text-base text-neutral-900 whitespace-pre-wrap leading-relaxed font-medium">{n.content}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 pt-4 border-t-2 border-neutral-500 grid grid-cols-3 gap-6 text-sm text-neutral-700 print-avoid-break">
        <div>
          <p className="font-bold text-neutral-900 uppercase tracking-wider text-xs mb-1">Întocmit</p>
          <div className="border-b-2 border-neutral-700 h-8"></div>
        </div>
        <div>
          <p className="font-bold text-neutral-900 uppercase tracking-wider text-xs mb-1">Brutar</p>
          <div className="border-b-2 border-neutral-700 h-8"></div>
        </div>
        <div>
          <p className="font-bold text-neutral-900 uppercase tracking-wider text-xs mb-1">Verificat</p>
          <div className="border-b-2 border-neutral-700 h-8"></div>
        </div>
      </div>
    </div>
  );
}
